import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../permissions";
import { IconBranch, IconLoader, IconPlus, IconAlert } from "../components/Icons";
import SearchableSelect from "../components/SearchableSelect";

interface Branch {
  id: string;
  code: string;
  name: string;
  currency: string;
  locale: string;
  is_active: boolean;
  has_logo?: boolean;
  has_header_logo?: boolean;
  header_color_a?: string | null;
  header_color_b?: string | null;
}

export default function Branches() {
  const { user } = useCurrentUser();
  const canWrite = can(user, "branches", "write");
  const { t } = useTranslation();
  const nav = useNavigate();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ code: "", name: "", currency: "CZK", locale: "cs" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [headerLogoUrl, setHeaderLogoUrl] = useState<string | null>(null);
  const [headerLogoBusy, setHeaderLogoBusy] = useState(false);
  const [headerColorA, setHeaderColorA] = useState<string | null>(null);
  const [headerColorB, setHeaderColorB] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const headerLogoInputRef = useRef<HTMLInputElement>(null);

  const DEFAULT_COLOR_A = "#ff6b00";
  const DEFAULT_COLOR_B = "#ffb347";

  async function loadHeaderLogo(branchId: string) {
    try {
      const { data } = await api.get<{ data_url: string }>(`/branches/${branchId}/header-logo`);
      setHeaderLogoUrl(data.data_url);
    } catch {
      setHeaderLogoUrl(null);
    }
  }

  async function uploadHeaderLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editId) return;
    const fd = new FormData();
    fd.append("file", file);
    setHeaderLogoBusy(true);
    setErr(null);
    try {
      const { data } = await api.put<{ data_url: string }>(`/branches/${editId}/header-logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setHeaderLogoUrl(data.data_url);
      await load();
    } catch (e2) {
      setErr(apiError(e2, t("branches.logoUploadError")));
    } finally {
      setHeaderLogoBusy(false);
    }
  }

  async function removeHeaderLogo() {
    if (!editId) return;
    setHeaderLogoBusy(true);
    setErr(null);
    try {
      await api.delete(`/branches/${editId}/header-logo`);
      setHeaderLogoUrl(null);
      await load();
    } catch (e2) {
      setErr(apiError(e2, t("branches.logoUploadError")));
    } finally {
      setHeaderLogoBusy(false);
    }
  }

  async function loadLogo(branchId: string) {
    try {
      const { data } = await api.get<{ data_url: string }>(`/branches/${branchId}/logo`);
      setLogoUrl(data.data_url);
    } catch {
      setLogoUrl(null);
    }
  }

  async function uploadLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editId) return;
    const fd = new FormData();
    fd.append("file", file);
    setLogoBusy(true);
    setErr(null);
    try {
      const { data } = await api.put<{ data_url: string }>(`/branches/${editId}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data.data_url);
      await load();
    } catch (e2) {
      setErr(apiError(e2, t("branches.logoUploadError")));
    } finally {
      setLogoBusy(false);
    }
  }

  async function removeLogo() {
    if (!editId) return;
    setLogoBusy(true);
    setErr(null);
    try {
      await api.delete(`/branches/${editId}/logo`);
      setLogoUrl(null);
      await load();
    } catch (e2) {
      setErr(apiError(e2, t("branches.logoUploadError")));
    } finally {
      setLogoBusy(false);
    }
  }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get<Branch[]>("/branches?limit=500");
      setBranches(data);
    } catch (e) {
      setErr(apiError(e, t("branches.loadError")));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/branches/${editId}`, { ...form, header_color_a: headerColorA, header_color_b: headerColorB });
      } else {
        await api.post("/branches", form);
      }
      setForm({ code: "", name: "", currency: "CZK", locale: "cs" });
      setEditId(null);
      setShowForm(false);
      await load();
    } catch (e) {
      setErr(apiError(e, t("branches.saveError")));
    } finally {
      setSaving(false);
    }
  }

  function startEdit(b: Branch) {
    setEditId(b.id);
    setForm({ code: b.code, name: b.name, currency: b.currency, locale: b.locale });
    setLogoUrl(null);
    setHeaderLogoUrl(null);
    setHeaderColorA(b.header_color_a ?? null);
    setHeaderColorB(b.header_color_b ?? null);
    if (b.has_logo) void loadLogo(b.id);
    if (b.has_header_logo) void loadHeaderLogo(b.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function cancelForm() {
    setShowForm(false);
    setEditId(null);
    setLogoUrl(null);
    setHeaderLogoUrl(null);
    setHeaderColorA(null);
    setHeaderColorB(null);
    setForm({ code: "", name: "", currency: "CZK", locale: "cs" });
  }

  const [deactivateFor, setDeactivateFor] = useState<{ branch: Branch; deviceCount: number } | null>(null);
  const [moveTarget, setMoveTarget] = useState("");
  const [deactivating, setDeactivating] = useState(false);

  async function deactivateBranch(b: Branch, moveTo?: string, deactivateDevices?: boolean) {
    setErr(null);
    setDeactivating(true);
    try {
      await api.put(`/branches/${b.id}`, {
        is_active: false,
        ...(moveTo ? { move_devices_to: moveTo } : {}),
        ...(deactivateDevices ? { deactivate_devices: true } : {}),
      });
      setDeactivateFor(null);
      setMoveTarget("");
      await load();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "devices_assigned") {
        setDeactivateFor({ branch: b, deviceCount: detail.count });
      } else {
        setErr(apiError(e, t("branches.saveError")));
      }
    } finally {
      setDeactivating(false);
    }
  }

  async function toggleActive(b: Branch) {
    if (b.is_active) {
      await deactivateBranch(b);
      return;
    }
    setErr(null);
    try {
      await api.put(`/branches/${b.id}`, { is_active: true });
      await load();
    } catch (e) {
      setErr(apiError(e, t("branches.saveError")));
    }
  }

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <IconLoader size={32} className="muted" />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1>{t("branches.title")}</h1>
          <p>{t("branches.subtitle")}</p>
        </div>
        {canWrite && (
        <button className="primary" onClick={() => (showForm ? cancelForm() : setShowForm(true))}>
          <IconPlus size={18} />
          {showForm ? t("common.cancel") : t("branches.newBranch")}
        </button>
        )}
      </div>

      {err && (
        <div className="card" style={{
          padding: "12px 16px", marginBottom: 20,
          background: "var(--red-dim)", color: "var(--red)",
          display: "flex", alignItems: "center", gap: 8, fontSize: 14,
        }}>
          <IconAlert size={16} />
          {err}
        </div>
      )}

      {canWrite && showForm && (
        <div className="card" style={{ padding: 24, marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>
            {editId ? t("branches.editBranch") : t("branches.newBranch")}
          </h3>
          <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
            <div>
              <label>{t("branches.name")} *</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("branches.namePlaceholder")}
                required
              />
            </div>
            <div>
              <label>{t("branches.code")} *</label>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder={t("branches.codePlaceholder")}
                required
              />
            </div>
            <div>
              <label>{t("branches.locale")}</label>
              <SearchableSelect
                value={form.locale}
                onChange={(val) => setForm({ ...form, locale: val })}
                options={[
                  { value: "cs", label: t("language.cs") },
                  { value: "en", label: t("language.en") },
                  { value: "sk", label: t("language.sk") },
                ]}
                searchPlaceholder={t("branches.searchLocale")}
              />
            </div>
            {editId && (
              <div style={{ gridColumn: "1 / -1" }}>
                <label>{t("branches.receiptLogo")}</label>
                <p className="muted" style={{ fontSize: 13, margin: "4px 0 10px" }}>{t("branches.receiptLogoHint")}</p>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  {logoUrl && (
                    <div style={{ padding: 10, background: "#fff", border: "1px solid var(--border)", borderRadius: 8 }}>
                      <img src={logoUrl} alt="logo" style={{ display: "block", maxWidth: 220, imageRendering: "pixelated" }} />
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8 }}>
                    <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/bmp" style={{ display: "none" }} onChange={(e) => void uploadLogo(e)} />
                    <button type="button" className="btn-ghost" onClick={() => logoInputRef.current?.click()} disabled={logoBusy}>
                      {logoBusy ? <IconLoader size={16} /> : t(logoUrl ? "branches.replaceLogo" : "branches.uploadLogo")}
                    </button>
                    {logoUrl && (
                      <button type="button" className="btn-ghost" onClick={() => void removeLogo()} disabled={logoBusy}>
                        {t("branches.removeLogo")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            {editId && (
              <div style={{ gridColumn: "1 / -1" }}>
                <label>{t("branches.headerBranding")}</label>
                <p className="muted" style={{ fontSize: 13, margin: "4px 0 10px" }}>{t("branches.headerBrandingHint")}</p>
                <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: 16 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                      {t("branches.headerColorA")}
                      <input
                        type="color"
                        value={headerColorA ?? DEFAULT_COLOR_A}
                        onChange={(e) => setHeaderColorA(e.target.value)}
                        style={{ width: 40, height: 32, padding: 2, border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer" }}
                      />
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                      {t("branches.headerColorB")}
                      <input
                        type="color"
                        value={headerColorB ?? DEFAULT_COLOR_B}
                        onChange={(e) => setHeaderColorB(e.target.value)}
                        style={{ width: 40, height: 32, padding: 2, border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer" }}
                      />
                    </label>
                    {(headerColorA || headerColorB) && (
                      <button type="button" className="btn-ghost" onClick={() => { setHeaderColorA(null); setHeaderColorB(null); }}>
                        {t("branches.headerColorsReset")}
                      </button>
                    )}
                  </div>
                  <div
                    style={{
                      height: 40, width: 240, borderRadius: 8, border: "1px solid var(--border)",
                      background: `linear-gradient(90deg, ${headerColorA ?? DEFAULT_COLOR_A}, ${headerColorB ?? DEFAULT_COLOR_B})`,
                      display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
                    }}
                  >
                    {headerLogoUrl && <img src={headerLogoUrl} alt="header logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input ref={headerLogoInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/bmp" style={{ display: "none" }} onChange={(e) => void uploadHeaderLogo(e)} />
                    <button type="button" className="btn-ghost" onClick={() => headerLogoInputRef.current?.click()} disabled={headerLogoBusy}>
                      {headerLogoBusy ? <IconLoader size={16} /> : t(headerLogoUrl ? "branches.replaceLogo" : "branches.uploadLogo")}
                    </button>
                    {headerLogoUrl && (
                      <button type="button" className="btn-ghost" onClick={() => void removeHeaderLogo()} disabled={headerLogoBusy}>
                        {t("branches.removeLogo")}
                      </button>
                    )}
                  </div>
                </div>
                <p className="muted" style={{ fontSize: 12, margin: "8px 0 0" }}>{t("branches.headerLogoHint")}</p>
              </div>
            )}
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 12 }}>
              <button type="submit" className="primary" disabled={saving}>
                {saving ? <IconLoader size={18} /> : editId ? t("common.save") : t("branches.create")}
              </button>
              {editId && (
                <button type="button" className="btn-ghost" onClick={cancelForm}>
                  {t("common.cancel")}
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {branches.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: "center" }}>
          <IconBranch size={48} className="muted" />
          <h3 style={{ marginTop: 16, fontSize: 18, fontWeight: 600 }}>{t("branches.emptyTitle")}</h3>
          <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 8, marginBottom: 20 }}>
            {t("branches.emptySubtitle")}
          </p>
          {canWrite && (
          <button className="primary" onClick={() => setShowForm(true)}>
            <IconPlus size={18} />
            {t("branches.createFirst")}
          </button>
          )}
        </div>
      ) : (
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>{t("branches.name")}</th>
                <th>{t("branches.code")}</th>
                <th>{t("branches.locale")}</th>
                <th>{t("common.status")}</th>
                <th>{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {branches.map((b) => (
                <tr key={b.id}>
                  <td style={{ fontWeight: 600 }}>{b.name}</td>
                  <td className="mono">{b.code}</td>
                  <td>{b.locale}</td>
                  <td>
                    <span className={b.is_active ? "badge badge-green" : "badge badge-red"}>
                      {b.is_active ? t("common.active") : t("common.inactive")}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn-ghost" onClick={() => nav(`/branches/${b.id}/floor-map`)}>
                        {t("branches.floorMap")}
                      </button>
                      {canWrite && (
                      <>
                      <button className="btn-ghost" onClick={() => startEdit(b)}>
                        {t("common.edit")}
                      </button>
                      <button className="btn-ghost" onClick={() => toggleActive(b)}>
                        {b.is_active ? t("common.deactivate") : t("common.activate")}
                      </button>
                      </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deactivateFor && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
          onClick={() => setDeactivateFor(null)}
        >
          <div
            className="card"
            style={{ width: "100%", maxWidth: 480, padding: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>{t("branches.deactivateTitle")}</h2>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.6 }}>
              {t("branches.deactivateDevicesInfo", { count: deactivateFor.deviceCount, name: deactivateFor.branch.name })}
            </p>
            <div style={{ marginTop: 16 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                {t("branches.moveDevicesTo")}
              </label>
              <select
                value={moveTarget}
                onChange={(e) => setMoveTarget(e.target.value)}
                style={{ width: "100%" }}
              >
                <option value="">{t("branches.selectTargetBranch")}</option>
                {branches
                  .filter((b) => b.is_active && b.id !== deactivateFor.branch.id)
                  .map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 24 }}>
              <button
                className="primary"
                onClick={() => void deactivateBranch(deactivateFor.branch, moveTarget)}
                disabled={!moveTarget || deactivating}
              >
                {deactivating ? <IconLoader size={16} /> : t("branches.moveAndDeactivate")}
              </button>
              <button
                className="btn-ghost"
                style={{ color: "var(--red)" }}
                onClick={() => void deactivateBranch(deactivateFor.branch, undefined, true)}
                disabled={deactivating}
              >
                {t("branches.deactivateWithDevices")}
              </button>
              <button onClick={() => setDeactivateFor(null)} disabled={deactivating}>
                {t("common.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
